/**
 * UNIT AWARD BUILDER — THEME-ONLY CART CLEANUP
 * ============================================
 * A no-app alternative to Cart Transform. It HIDES the builder's line items in
 * the cart (cart page + cart drawer) and shows one tidy summary instead. The
 * items are still really in the cart — they're charged, they deduct inventory,
 * and they appear at checkout and on the order/invoice. This only changes what
 * the customer SEES in the cart.
 *
 * ⚠️  Do NOT run this at the same time as the Cart Transform function. Pick one
 *     approach. (If you later deploy the function, remove this script.)
 *
 * Works with the `unit-award-builder-ct` builder, which stamps every line with
 * `_Build ID` and `_Bundle Title`. It also degrades gracefully with the
 * original builder (it will fall back to a generic title).
 *
 * HOW IT WORKS
 *   1. Reads the live cart from /cart.js.
 *   2. Finds the cart rows that belong to a build (lines with a `_Build ID`,
 *      or just the fee lines, depending on MODE).
 *   3. Hides those rows and injects one summary block per build.
 *   It re-runs whenever the cart re-renders (drawer open, qty change, etc.).
 *
 * TUNING: if rows don't hide on your theme, adjust ROW_SELECTORS /
 * ITEMS_CONTAINERS below — see THEME-ONLY-GUIDE.md.
 */
(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────────────────
  // CONFIG
  // ─────────────────────────────────────────────────────────────────────────
  var CONFIG = {
    // 'bundle' = hide ALL of a build's lines and show one summary (clean total).
    // 'fees'   = hide ONLY the fee lines; leave rack/ribbons/devices visible.
    //            (Note: in 'fees' mode the cart subtotal still includes the
    //            fees, so the visible rows won't sum to the subtotal.)
    MODE: 'bundle',

    // Property keys set by the builder.
    BUILD_ID_PROP: '_Build ID',
    BUNDLE_TITLE_PROP: '_Bundle Title',
    FEE_PROP: '_fee_item',

    // Show an expandable "View items" list inside the summary (bundle mode).
    SHOW_ITEM_LIST: true,

    // Show a "Remove this build" button on the summary (bundle mode). It removes
    // every line of that build from the cart in one action (two-tap confirm).
    SHOW_REMOVE: true,

    // Candidate selectors for a single cart line row. First match per container
    // that returns the same count as the cart wins (positional matching).
    // NOTE: `.js-cart-item` is used by THIS theme on BOTH the cart page
    // (tr.cart-item.js-cart-item) and the mini-cart drawer
    // (.mini-cart-item.js-cart-item), so it's listed first.
    ROW_SELECTORS: [
      '[data-uab-line]',
      '.js-cart-item',
      'tr.cart-item',
      '.mini-cart-item',
      'li.cart-item',
      '.cart-item',
      '.cart__row',
      '.cart-items__row',
      '[data-cart-item]',
      '.line-item',
      '.cart-line-item'
    ],

    // Candidate selectors for the element that wraps all the rows (page + drawer).
    // This theme: `.mini-cart-item-list` (drawer) and `table.cart-items` (page).
    // Overlapping/nested matches are de-duplicated to the innermost in
    // getContainers(), so listing both an outer and inner wrapper is fine.
    ITEMS_CONTAINERS: [
      '.mini-cart-item-list',
      '#header-mini-cart-content',
      '#main-cart-items',
      '.cart-items',
      '.cart__items',
      '[data-cart-items]',
      'cart-drawer-items',
      '.drawer__cart-items',
      '.cart__contents',
      '.cart-drawer__items'
    ],

    DEBUG: false
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────
  function log() { if (CONFIG.DEBUG) try { console.log.apply(console, ['[UAB cart]'].concat([].slice.call(arguments))); } catch (e) {} }

  function money(cents) {
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
      }
    } catch (e) {}
    return '$' + (cents / 100).toFixed(2);
  }

  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  // A cart line "belongs to a build" if it carries the _Build ID property.
  function lineBuildId(line) {
    var p = line.properties || {};
    return p[CONFIG.BUILD_ID_PROP] || null;
  }
  function lineIsFee(line) {
    var p = line.properties || {};
    var v = p[CONFIG.FEE_PROP];
    return v === true || v === 'true' || v === '1';
  }
  function lineBundleTitle(line) {
    var p = line.properties || {};
    return p[CONFIG.BUNDLE_TITLE_PROP] || null;
  }

  // Which cart-item indexes should be hidden, given the mode.
  function indexesToHide(cart) {
    var set = {};
    cart.items.forEach(function (line, i) {
      if (CONFIG.MODE === 'fees') {
        if (lineIsFee(line)) set[i] = true;
      } else { // bundle
        if (lineBuildId(line)) set[i] = true;
      }
    });
    return set;
  }

  // Group build lines for the summary (bundle mode).
  function buildGroups(cart) {
    var groups = {}; // buildId -> { title, total, keys:[], items:[{title,qty,fee}] }
    cart.items.forEach(function (line) {
      var bid = lineBuildId(line);
      if (!bid) return;
      if (!groups[bid]) groups[bid] = { id: bid, title: null, total: 0, keys: [], items: [] };
      var g = groups[bid];
      g.total += (typeof line.final_line_price === 'number' ? line.final_line_price : line.line_price) || 0;
      if (line.key) g.keys.push(line.key);
      if (!g.title && lineBundleTitle(line)) g.title = lineBundleTitle(line);
      g.items.push({
        title: line.product_title || line.title || 'Item',
        variant: (line.variant_title && line.variant_title !== 'Default Title') ? line.variant_title : '',
        qty: line.quantity,
        fee: lineIsFee(line)
      });
    });
    // Fallback title
    Object.keys(groups).forEach(function (k) { if (!groups[k].title) groups[k].title = 'Custom Unit Award Rack'; });
    return groups;
  }

  // Find the row elements inside a container, using the first selector whose
  // count matches the cart length (positional matching is the most portable).
  function rowsForContainer(container, expectedCount) {
    for (var i = 0; i < CONFIG.ROW_SELECTORS.length; i++) {
      var rows = container.querySelectorAll(CONFIG.ROW_SELECTORS[i]);
      if (rows.length && (rows.length === expectedCount || expectedCount === -1)) {
        return [].slice.call(rows);
      }
    }
    // No exact-count match: return the densest selector result as a best effort.
    var best = [];
    CONFIG.ROW_SELECTORS.forEach(function (sel) {
      var r = container.querySelectorAll(sel);
      if (r.length > best.length) best = [].slice.call(r);
    });
    return best;
  }

  function getContainers() {
    var found = [];
    CONFIG.ITEMS_CONTAINERS.forEach(function (sel) {
      [].slice.call(document.querySelectorAll(sel)).forEach(function (el) {
        if (found.indexOf(el) === -1) found.push(el);
      });
    });
    // Keep only the innermost containers: drop any element that contains
    // another matched element. This avoids processing the same rows twice
    // (e.g. #main-cart-items wrapping table.cart-items) and double summaries.
    return found.filter(function (el) {
      return !found.some(function (other) { return other !== el && el.contains(other); });
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  function clearPrevious() {
    [].slice.call(document.querySelectorAll('[data-uab-hidden]')).forEach(function (el) {
      el.removeAttribute('data-uab-hidden');
    });
    [].slice.call(document.querySelectorAll('.uab-cart-summary')).forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });
  }

  function summaryEl(group) {
    var wrap = document.createElement('div');
    wrap.className = 'uab-cart-summary';
    var itemsHtml = '';
    if (CONFIG.SHOW_ITEM_LIST && CONFIG.MODE === 'bundle') {
      var visibleItems = group.items.filter(function (it) { return !it.fee; }); // keep fees invisible
      if (visibleItems.length) {
        itemsHtml =
          '<details class="uab-cart-summary__details"' + (expandedBuilds[group.id] ? ' open' : '') + '>' +
            '<summary class="uab-cart-summary__toggle">View items</summary>' +
            '<ul class="uab-cart-summary__list">' +
              visibleItems.map(function (it) {
                return '<li><span>' + esc(it.title) + (it.variant ? ' — ' + esc(it.variant) : '') +
                  '</span><span>×' + it.qty + '</span></li>';
              }).join('') +
            '</ul>' +
          '</details>';
      }
    }
    var removeHtml = '';
    if (CONFIG.SHOW_REMOVE && CONFIG.MODE === 'bundle' && group.keys && group.keys.length) {
      removeHtml =
        '<div class="uab-cart-summary__footer">' +
          '<button type="button" class="uab-cart-summary__remove" data-uab-remove ' +
            'aria-label="Remove this build from your cart">Remove this build</button>' +
        '</div>';
    }

    wrap.innerHTML =
      '<div class="uab-cart-summary__row">' +
        '<span class="uab-cart-summary__icon" aria-hidden="true">★</span>' +
        '<span class="uab-cart-summary__title">' + esc(group.title) + '</span>' +
        '<span class="uab-cart-summary__price">' + money(group.total) + '</span>' +
      '</div>' + itemsHtml + removeHtml;

    var rm = wrap.querySelector('[data-uab-remove]');
    if (rm) bindRemove(rm, group.id);

    var det = wrap.querySelector('.uab-cart-summary__details');
    if (det) det.addEventListener('toggle', function () {
      if (det.open) expandedBuilds[group.id] = true;
      else delete expandedBuilds[group.id];
    });

    return wrap;
  }

  // Which build (if any) is currently "armed" for removal. Stored OUTSIDE the
  // DOM so a redraw of the summary (which Chrome does at different times than
  // Safari) can't cancel an in-progress two-tap confirm.
  var armedBuildId = null;
  var armTimer = null;

  // Remember which builds have their "View items" panel expanded, so a redraw
  // of the summary doesn't collapse it.
  var expandedBuilds = {};

  function disarm() {
    armedBuildId = null;
    if (armTimer) { clearTimeout(armTimer); armTimer = null; }
  }

  // Two-tap confirm, then remove every line of the build.
  function bindRemove(btn, buildId) {
    // If this build is already armed (e.g. the summary was just redrawn between
    // taps), render the button in its armed state so the next click confirms.
    if (armedBuildId === buildId) {
      btn.classList.add('is-armed');
      btn.textContent = 'Click again to remove';
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (!buildId) return;

      if (armedBuildId !== buildId) {
        armedBuildId = buildId;
        btn.classList.add('is-armed');
        btn.textContent = 'Click again to remove';
        if (armTimer) clearTimeout(armTimer);
        armTimer = setTimeout(function () {
          disarm();
          btn.classList.remove('is-armed');
          btn.textContent = 'Remove this build';
        }, 4000);
        return;
      }

      disarm();
      btn.disabled = true;
      btn.classList.remove('is-armed');
      btn.textContent = 'Removing…';
      removeBuild(buildId);
    });
  }

  // Re-read the cart NOW (so keys are never stale), find every line with this
  // build's _Build ID, and remove each one via /cart/change.js. We remove
  // sequentially because each change re-resolves the cart server-side.
  function removeBuild(buildId) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var keys = cart.items
          .filter(function (l) { return l.properties && l.properties[CONFIG.BUILD_ID_PROP] === buildId; })
          .map(function (l) { return l.key; });

        // Chain change.js calls one after another.
        return keys.reduce(function (chain, key) {
          return chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ id: key, quantity: 0 })
            });
          });
        }, Promise.resolve());
      })
      .then(function () { afterRemove(); })
      .catch(function () { window.location.reload(); });
  }

  // Reflect the removal: refresh this theme's mini-cart drawer in place + the
  // cart count, then re-apply our summaries. On the dedicated cart page, the
  // theme's cart web-component won't reflect an external change, so reload.
  function afterRemove() {
    fetch('/?sections=header-mini-cart-content,header-mini-cart-footer', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (sections) {
        if (sections) {
          var c = document.getElementById('header-mini-cart-content');
          if (c && sections['header-mini-cart-content'] != null) c.innerHTML = sections['header-mini-cart-content'];
          var f = document.getElementById('header-mini-cart-footer');
          if (f && sections['header-mini-cart-footer'] != null) f.innerHTML = sections['header-mini-cart-footer'];
        }
        return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        document.querySelectorAll('.head-slot-cart-link-quantity, .cart-count, .cart-count-bubble, [data-cart-count]')
          .forEach(function (el) { el.textContent = cart.item_count; });
        if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
        apply();
      })
      .catch(function () { window.location.reload(); });
  }

  function render(cart) {
    clearPrevious();
    if (!cart || !cart.items || !cart.items.length) return;

    var hideSet = indexesToHide(cart);
    var anyHidden = Object.keys(hideSet).length > 0;
    if (!anyHidden) { log('nothing to hide'); return; }

    var groups = CONFIG.MODE === 'bundle' ? buildGroups(cart) : null;
    var containers = getContainers();
    log('containers', containers.length, 'hide', Object.keys(hideSet));

    containers.forEach(function (container) {
      var rows = rowsForContainer(container, cart.items.length);

      // Hide rows by position when counts line up; this is the portable path.
      if (rows.length === cart.items.length) {
        rows.forEach(function (row, i) {
          if (hideSet[i]) row.setAttribute('data-uab-hidden', '1');
        });
      } else {
        // Fallback: try to match each hidden line to a row by its key/index.
        cart.items.forEach(function (line, i) {
          if (!hideSet[i]) return;
          var row = findRowByKey(container, line.key, i + 1);
          if (row) row.setAttribute('data-uab-hidden', '1');
        });
      }

      // Inject one summary per build at the top of this container (bundle mode).
      if (CONFIG.MODE === 'bundle' && groups) {
        var keys = Object.keys(groups);
        // Insert in reverse so the first build ends up on top.
        for (var k = keys.length - 1; k >= 0; k--) {
          var el = summaryEl(groups[keys[k]]);
          el.setAttribute('data-uab-build', keys[k]);
          insertSummary(container, el);
        }
      }
    });
  }

  function insertSummary(container, el) {
    // Prefer placing above the rows but inside the cart UI. For <table>/<tbody>
    // containers, insert before the container element to keep valid markup.
    var tag = container.tagName.toLowerCase();
    if (tag === 'tbody' || tag === 'table' || tag === 'ul' || tag === 'ol') {
      container.parentNode.insertBefore(el, container);
    } else {
      container.insertBefore(el, container.firstChild);
    }
  }

  // Best-effort key/index matching for themes whose row count != cart count.
  function findRowByKey(container, key, index1) {
    var sels = [
      '[data-key="' + cssAttr(key) + '"]',
      '[data-cart-item-key="' + cssAttr(key) + '"]',
      '[data-line-item-key="' + cssAttr(key) + '"]',
      '#CartItem-' + index1,
      '[data-index="' + index1 + '"]'
    ];
    for (var i = 0; i < sels.length; i++) {
      var el;
      try { el = container.querySelector(sels[i]); } catch (e) { el = null; }
      if (el) return closestRow(el);
    }
    // key embedded in an id/name somewhere
    try {
      var byName = container.querySelector('[name="updates[' + cssAttr(key) + ']"]');
      if (byName) return closestRow(byName);
    } catch (e) {}
    return null;
  }

  function closestRow(el) {
    for (var i = 0; i < CONFIG.ROW_SELECTORS.length; i++) {
      var r = el.closest(CONFIG.ROW_SELECTORS[i]);
      if (r) return r;
    }
    return el;
  }

  function cssAttr(s) { return String(s).replace(/(["\\])/g, '\\$1'); }

  // ─────────────────────────────────────────────────────────────────────────
  // Apply + observe
  // ─────────────────────────────────────────────────────────────────────────
  var observer = null;
  var pending = null;

  function apply() {
    fetchCart().then(function (cart) {
      if (observer) observer.disconnect();
      try { render(cart); } catch (e) { log('render error', e); }
      connectObserver();
    }).catch(function (e) { log('cart fetch failed', e); });
  }

  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(apply, 150);
  }

  // Only watch the cart containers — NOT the whole page. Watching document.body
  // made unrelated widgets (chat, loyalty bars, etc.) trigger constant redraws,
  // which collapsed the open "View items" panel (worse in Chrome than Safari).
  var OBSERVE_TARGETS = [
    '#header-mini-cart-content',
    '#main-cart-items',
    '.cart-drawer', '#CartDrawer', 'cart-drawer', 'cart-notification'
  ];

  function connectObserver() {
    if (!('MutationObserver' in window)) return;
    if (!observer) {
      observer = new MutationObserver(function (mutations) {
        // Ignore mutations caused by our OWN summary UI (e.g. the remove
        // button's two-tap label change or expanding "View items").
        for (var i = 0; i < mutations.length; i++) {
          var t = mutations[i].target;
          var el = (t && t.nodeType === 1) ? t : (t && t.parentElement);
          if (el && el.closest && el.closest('.uab-cart-summary')) continue;
          schedule();
          return;
        }
      });
    }
    var watched = 0;
    OBSERVE_TARGETS.forEach(function (sel) {
      [].slice.call(document.querySelectorAll(sel)).forEach(function (node) {
        observer.observe(node, { childList: true, subtree: true });
        watched++;
      });
    });
    // Fallback: if no cart container exists yet, watch the body so we still
    // notice the cart appearing (still filtered to ignore our own UI).
    if (!watched) observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    apply();
    // Re-apply on common theme cart events too (covers AJAX add/update).
    ['cart:updated', 'cart:refresh', 'cart:change', 'ajaxCart.afterCartLoad'].forEach(function (evt) {
      document.addEventListener(evt, schedule);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
